/**
 * Détecte la couleur d'un vin à partir de sa catégorie d'inventaire
 * (priorité) et de son nom (fallback).
 *
 * Catégorie : si l'admin a créé des catégories "Vins rouges",
 * "Vins blancs", etc., on les détecte. Fiable car structuré.
 * Nom : fallback si la catégorie est générique (ex: "Vins") — on
 * regex le mot rouge/blanc/rosé/bulles dans le nom de l'item.
 */
export type WineColor = 'red' | 'white' | 'rose' | 'sparkling';

const PATTERNS: Array<{ color: WineColor; regex: RegExp }> = [
  // Effervescents AVANT les rouges/blancs (souvent "champagne brut" sans
  // mention rouge/blanc, on veut le classer sparkling).
  {
    color: 'sparkling',
    regex: /\b(bulles?|brut|champagne|prosecco|cr[eé]mant|moussant|p[eé]tillant|cava|spumante|effervescents?)\b/i,
  },
  { color: 'rose', regex: /\bros[eé]s?\b/i },
  { color: 'white', regex: /\b(blancs?|blanche|whites?)\b/i },
  { color: 'red', regex: /\b(rouges?|reds?)\b/i },
];

function normalize(s: string): string {
  return s
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '');
}

function matchPattern(text: string): WineColor | null {
  const n = normalize(text);
  for (const { color, regex } of PATTERNS) {
    if (regex.test(n)) return color;
  }
  return null;
}

/**
 * Détecte la couleur en regardant d'abord la catégorie d'inventaire
 * (signal le plus fiable car structuré par l'admin), puis le nom
 * en fallback.
 */
export function inferWineColor(
  name: string,
  categoryLabel?: string | null
): WineColor | null {
  // 1. Priorité : la catégorie ("Vins rouges" → red)
  if (categoryLabel) {
    const fromCategory = matchPattern(categoryLabel);
    if (fromCategory) return fromCategory;
  }
  // 2. Fallback : le nom de l'item
  return matchPattern(name);
}

export const WINE_COLOR_LABELS: Record<WineColor, string> = {
  red: 'Rouge',
  white: 'Blanc',
  rose: 'Rosé',
  sparkling: 'Bulles',
};

/**
 * Classes Tailwind pour styliser un badge selon la couleur du vin.
 * Texte + bordure assortis, fond translucide.
 */
export const WINE_COLOR_BADGE_CLASS: Record<WineColor, string> = {
  red: 'border-red-700/50 text-red-300 bg-red-900/20',
  white: 'border-amber-200/40 text-amber-100 bg-amber-100/5',
  rose: 'border-pink-400/40 text-pink-300 bg-pink-500/10',
  sparkling: 'border-yellow-400/40 text-yellow-200 bg-yellow-500/10',
};
